import { Stack } from 'expo-router'
import Head from 'expo-router/head'

import { GluestackUIProvider } from '@/components/ui/gluestack-ui-provider'
import '@/global.css'
import { NavigationDarkTheme, NavigationLightTheme } from '@/theme'
import RootLayout from '@/views/layouts/RootLayout'
import { ActionSheetProvider } from '@expo/react-native-action-sheet'
import { ThemeProvider, useTheme } from '@react-navigation/native'
import { NativeStackNavigationOptions } from '@react-navigation/native-stack'
import * as Sentry from '@sentry/react-native'
import { useColorScheme } from 'nativewind'
import type { PropsWithChildren } from 'react'
import { LogBox, Platform } from 'react-native'

// dev build 上 LogBox 全屏 modal 会因 RC 等 console.error 反复弹起，
// 盖住屏幕下半区按钮（典型受害者：登录 sheet 的 CTA），导致 E2E 测试 tap 命中 LogBox。
// 这里只静默 LogBox UI，console.error 本身保留以便 Metro 仍能看到日志。
if (__DEV__) {
  LogBox.ignoreAllLogs()
}

Sentry.init({
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
  enabled: !__DEV__,
  environment: __DEV__ ? 'development' : 'production',
  sendDefaultPii: true,
  tracesSampleRate: 0.1,
})

function Layout() {
  return (
    <>
      {Platform.OS === 'web' && (
        <Head>
          {/* eslint-disable-next-line react-native/no-raw-text */}
          <title>金数据</title>
        </Head>
      )}
      <GluestackUIProvider mode="system">
        <NavigationThemeProvider>
          <RootLayout>
            <ActionSheetProvider>
              <RootStackNavigator />
            </ActionSheetProvider>
          </RootLayout>
        </NavigationThemeProvider>
      </GluestackUIProvider>
    </>
  )
}

export default Sentry.wrap(Layout)

function RootStackNavigator() {
  const theme = useTheme()

  return (
    <Stack
      screenOptions={{
        title: '',
        headerShown: false,
        headerLargeTitle: true,
        headerShadowVisible: false,
        headerBackButtonDisplayMode: 'minimal',
        headerTransparent: true,
        contentStyle: { backgroundColor: theme.colors.background },
      }}
    >
      <Stack.Screen name="onboarding" options={{ animation: 'none' }} />
      <Stack.Screen name="login/index" />
      <Stack.Screen
        name="login/sms"
        options={{
          headerShown: true,
          title: '使用手机登录',
          headerLargeTitle: false,
          headerTransparent: false,
          ...Platform.select<NativeStackNavigationOptions>({
            ios: {
              presentation: 'formSheet',
              sheetAllowedDetents: 'fitToContents',
              sheetGrabberVisible: true,
            },
          }),
        }}
      />
      <Stack.Screen
        name="login/identity"
        options={{
          headerShown: true,
          title: '使用密码登录',
          headerLargeTitle: false,
          headerTransparent: false,
          ...Platform.select<NativeStackNavigationOptions>({
            ios: {
              presentation: 'formSheet',
              sheetAllowedDetents: 'fitToContents',
              sheetGrabberVisible: true,
            },
          }),
        }}
      />
      <Stack.Screen
        name="login/complete_registration"
        options={{
          headerShown: true,
          title: '完善账户',
          headerLargeTitle: false,
          ...Platform.select<NativeStackNavigationOptions>({
            ios: {
              presentation: 'formSheet',
              sheetAllowedDetents: 'fitToContents',
              sheetGrabberVisible: true,
            },
            android: {
              headerTransparent: false,
              headerStyle: { backgroundColor: theme.colors.background },
            },
          }),
        }}
      />
      <Stack.Screen
        name="login/switch_user"
        options={{
          headerShown: true,
          title: '选择团队',
          ...Platform.select<NativeStackNavigationOptions>({
            android: {
              headerLargeTitle: false,
              headerTransparent: false,
              headerStyle: { backgroundColor: theme.colors.background },
            },
          }),
        }}
      />
      <Stack.Screen name="(system)" />
      <Stack.Screen
        name="(leagal)/usage_specification"
        options={{
          headerShown: true,
          title: '',
          ...Platform.select<NativeStackNavigationOptions>({
            android: {
              headerLargeTitle: false,
              headerTransparent: false,
              headerStyle: { backgroundColor: theme.colors.background },
            },
          }),
        }}
      />
      <Stack.Screen
        name="(leagal)/privacy"
        options={{
          headerShown: true,
          title: '',
          ...Platform.select<NativeStackNavigationOptions>({
            android: {
              headerLargeTitle: false,
              headerTransparent: false,
              headerStyle: { backgroundColor: theme.colors.background },
            },
          }),
        }}
      />
      <Stack.Screen
        name="(leagal)/terms"
        options={{
          headerShown: true,
          title: '',
          ...Platform.select<NativeStackNavigationOptions>({
            android: {
              headerLargeTitle: false,
              headerTransparent: false,
              headerStyle: { backgroundColor: theme.colors.background },
            },
          }),
        }}
      />
    </Stack>
  )
}

function NavigationThemeProvider({ children }: PropsWithChildren) {
  const { colorScheme } = useColorScheme()
  const theme = colorScheme === 'dark' ? NavigationDarkTheme : NavigationLightTheme

  return <ThemeProvider value={theme}>{children}</ThemeProvider>
}
